import esriConfig from "@arcgis/core/config";

const GLOBAL_KEY = "__KLIMAVIEWER_MAPSERVER_EXPORT_MONITOR__";

/**
 * @typedef {Object} MapServerExportError
 * @property {number} at - Epoch ms when the error was observed.
 * @property {string} url - Best-effort request URL (may be empty).
 * @property {number | undefined | null} httpStatus - HTTP status if available.
 * @property {string} message - Best-effort error message.
 * @property {Record<string, any> | undefined} query - Best-effort query params (export request parameters).
 */

/**
 * @typedef {Object} MapServerExportMonitorState
 * @property {number} exportPendingCount - Number of in-flight `/MapServer/export` requests.
 * @property {MapServerExportError | null} exportError - Last export error we show to the user.
 */

/**
 * @typedef {Object} MapServerExportMonitor
 * @property {() => MapServerExportMonitorState} getState
 * @property {(listener: (state: MapServerExportMonitorState) => void) => () => void} subscribe
 * @property {() => void} clearExportError
 * @property {(err: MapServerExportError | null) => string} formatExportErrorForTooltip
 */

/**
 * Tracks ArcGIS `/MapServer/export` network activity via `esriConfig.request.interceptors`.
 *
 * Design goals:
 * - singleton (avoid duplicate interceptors; supports hot reload)
 * - ignore expected cancellations (zoom/pan aborts)
 * - only show real failures (HTTP 4xx/5xx, timeouts, network errors)
 * - clear error on next successful export (recovery signal)
 *
 * @returns {MapServerExportMonitor}
 */
function createMonitor() {
  /** @type {number} */
  let exportPendingCount = 0;
  /** @type {MapServerExportError | null} */
  let exportError = null;
  const listeners = new Set();

  let installed = false;

  const emit = () => {
    /** @type {MapServerExportMonitorState} */
    const snapshot = { exportPendingCount, exportError };
    listeners.forEach((fn) => {
      try {
        fn(snapshot);
      } catch (e) {
        // ignore listener errors
      }
    });
  };

  /**
   * Classifies errors that are expected user-navigation aborts (e.g. pan/zoom cancels
   * an in-flight export request). These should not be shown as user-facing failures.
   */
  const isLikelyCanceledRequest = (err) => {
    const name = String(err?.name || "").toLowerCase();
    const msg = String(err?.message || "").toLowerCase();
    const detailsMsg = String(err?.details?.message || "").toLowerCase();
    const code = String(err?.details?.error?.code || "").toLowerCase();

    if (name === "aborterror") return true;
    if (
      msg.includes("abort") ||
      msg.includes("canceled") ||
      msg.includes("cancelled")
    )
      return true;
    if (
      detailsMsg.includes("abort") ||
      detailsMsg.includes("canceled") ||
      detailsMsg.includes("cancelled")
    )
      return true;
    if (code === "aborterror" || code === "aborted") return true;
    return false;
  };

  /**
   * Returns true for failures we should show to users (HTTP 4xx/5xx, timeouts,
   * and network errors). Returns false for expected aborts/cancels.
   */
  const isRealFailure = (err) => {
    const httpStatus = err?.details?.httpStatus || err?.response?.status;
    const msg = String(err?.message || "").toLowerCase();
    const detailsMsg = String(err?.details?.message || "").toLowerCase();

    if (typeof httpStatus === "number") {
      if (httpStatus === 0) return true; // network/CORS-ish
      if (httpStatus === 408) return true; // timeout
      if (httpStatus === 499) return false; // client-cancel (common in some proxies)
      return httpStatus >= 400;
    }

    if (msg.includes("timeout") || detailsMsg.includes("timeout")) return true;
    if (msg.includes("network") || detailsMsg.includes("network")) return true;
    if (
      msg.includes("failed to fetch") ||
      detailsMsg.includes("failed to fetch")
    )
      return true;

    return false;
  };

  const clearExportError = () => {
    exportError = null;
    emit();
  };

  const ensureInstalled = () => {
    if (installed) return;
    if (!esriConfig?.request?.interceptors?.push) return;

    // Avoid duplicate interceptors (e.g., hot reload) by scanning existing ones.
    const alreadyThere = esriConfig.request.interceptors.some(
      (itc) => itc && itc.__klimaviewerExportMonitor === true
    );
    if (alreadyThere) {
      installed = true;
      return;
    }

    const interceptor = {
      __klimaviewerExportMonitor: true,
      urls: /\/MapServer\/export/i,
      before: () => {
        exportPendingCount += 1;
        emit();
      },
      after: () => {
        exportPendingCount = Math.max(0, exportPendingCount - 1);
        // Successful export indicates recovery.
        clearExportError();
      },
      error: (err) => {
        exportPendingCount = Math.max(0, exportPendingCount - 1);

        // Zoom/pan cancels are expected.
        if (isLikelyCanceledRequest(err)) {
          emit();
          return;
        }

        if (!isRealFailure(err)) {
          emit();
          return;
        }
        // console.log(
        //   "[MapServerExportMonitor] Real export failure detected:",
        //   err
        // );

        const url =
          err?.details?.url ||
          err?.requestOptions?.url ||
          err?.response?.url ||
          err?.url ||
          "";
        const httpStatus = err?.details?.httpStatus || err?.response?.status;
        const message = err?.message || "MapServer export request failed";
        // Capture export params. ArcGIS errors commonly expose these under `err.requestOptions.query`.
        const query =
          err?.requestOptions?.query ||
          err?.requestOptions?.queries ||
          err?.details?.requestOptions?.query ||
          err?.details?.requestOptions?.queries;

        exportError = { at: Date.now(), url, httpStatus, message, query };

        emit();
      },
    };

    esriConfig.request.interceptors.push(interceptor);
    installed = true;
  };

  /** @returns {MapServerExportMonitorState} */
  const getState = () => ({ exportPendingCount, exportError });

  const subscribe = (listener) => {
    ensureInstalled();
    listeners.add(listener);
    // emit current state immediately
    try {
      listener(getState());
    } catch (e) {
      // ignore
    }

    return () => {
      listeners.delete(listener);
    };
  };

  /** @type {MapServerExportMonitor["formatExportErrorForTooltip"]} */
  const formatExportErrorForTooltip = (err) => {
    if (!err) return "";
    const prefix = err.httpStatus ? `HTTP ${err.httpStatus}: ` : "";
    let queryText = "";
    if (err.query && typeof err.query === "object") {
      const prettyPrint = (v) => {
        if (v === undefined || v === null) return null;
        if (Array.isArray(v))
          return `[${v.map(prettyPrint).filter(Boolean).join(", ")}]`;
        if (typeof v === "object") return JSON.stringify(v);
        return JSON.stringify(v);
      };
      const entries = Object.entries(err.query)
        .map(([k, v]) => {
          const val = prettyPrint(v);
          return val !== null ? `${k}: ${val}` : null;
        })
        .filter(Boolean)
        .join("; ");
      if (entries) {
        queryText = `\nQuery-Parameter: ${entries}`;
      }
    }
    return `${prefix}${err.message}${
      err.url ? `\n${err.url}` : ""
    }${queryText}`;
  };

  return {
    getState,
    subscribe,
    clearExportError,
    formatExportErrorForTooltip,
  };
}

const globalObj = typeof window !== "undefined" ? window : {};
if (!globalObj[GLOBAL_KEY]) {
  globalObj[GLOBAL_KEY] = createMonitor();
}

export default globalObj[GLOBAL_KEY];
